/*
 *   File : list.js
 *   Author : https://evoqins.com
 *   Description : list page 
 *   Integrations : 'NA'
 *   Version : 1.0.0
*/

// custom packages
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { Modal } from 'bootstrap/dist/js/bootstrap.min';

// custom components
import { APIService } from '../../Service';
import { Chip, CustomTab } from '../../Components/Others';
import { CustomDatePicker, CustomSearchInput, CustomSelectBox, DateRangePicker } from '../../Components/FormElements';
import { PrimaryButton, SecondaryButton, TextButton } from '../../Components/Buttons';
import { CustomDataTable } from '../../Components/DataTable';
import { Icon } from '../../Components/Icon';
import { AcceptModal, RejectModal, ViewDetailModal } from '../../Components/Modal';
import EmptyScreen from '../../Components/Others/empty-screen';

// custom styles
import color from '../../Style/color.scss';

const TAB_TITLES = [
    { id: 1, type: "invested", label: 'Invested', tooltip: false },
    { id: 2, type: "non_invested", label: 'Non-Invested', tooltip: true, tooltipContent: 'User who created investment account and didn’t invested yet' },
    { id: 3, type: "signed_up", label: 'Signed-up', tooltip: true, tooltipContent: 'User who signed up and left before creating investment account' },
    { id: 4, type: "fresh_kyc_submitted", label: 'Fresh KYC submitted', tooltip: true, tooltipContent: 'User who we have recently submitted their KYC documents' },
    { id: 5, type: "bank_verification_list", label: 'Bank verification list', tooltip: false }
];

const CustomerSupport = () => {

    const { state } = useLocation();
    const isInitializedRef = useRef(false);
    const isClearRef = useRef(false);
    const scrollRef = useRef(null);
    const navigate = useNavigate();

    const [selectedTab, setSelectedTab] = useState(-1);
    const [currentPage, setCurrentPage] = useState(1);

    const [selectedType, setSelectedType] = useState("invested");

    const [selectedKycStatus, setSelectedKycStatus] = useState(null);

    const [selectedRiskAppetite, setSelectedRiskAppetite] = useState("");
    const [selectedFpStatus, setSelectedFpStatus] = useState("");

    const [selectedStatus, setSelectedStatus] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [dateError, setDateError] = useState("");

    const [filterBtn, setFilterBtn] = useState(false);
    const [calendarToggle, setCalendarToggle] = useState(false);
    const [loader, setLoader] = useState(true);
    const [filterLoader, setFilterLoader] = useState(true);
    const [acceptModal, setAcceptModal] = useState(false);
    const [rejectModal, setRejectModal] = useState(false);
    const [onPaginationChange, setOnPaginationChange] = useState(false);
    const [proofModal, setProofModal] = useState(false);
    const [searched, setSearched] = useState(false);

    const [selectedDate, setSelectedDate] = useState(null);

    const [filterData, setFilterData] = useState({});

    const [listData, setListData] = useState({});
    const [filterTab, setFilterTab] = useState({});
    const [bankVerificationFilter, setBankVerificationFilter] = useState([]);
    const [fpStatusFilter, setFpStatusFilter] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});

    const [checked, setChecked] = useState([]);
    const [downloadLoader, setDownloadLoader] = useState(false);

    const [rejectSuccess, setRejectSuccess] = useState(false);
    const [rejectLoader, setRejectLoader] = useState(false);

    const [customerCategory, setCustomerCategory] = useState(null);
    const [customerType, setCustomerType] = useState(null);


    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        if (isInitializedRef.current === false) {
            _filterData();
            isInitializedRef.current = true;
        }
    }, []);

    useEffect(() => {
        setSearchValue("");
        if (selectedTab != -1) {
            _userList();
            isClearRef.current = false;
        }
    }, [selectedTab, isClearRef.current]);

    useEffect(() => {
        _handleBackdrop();
        // Add the event listener
        window.addEventListener('popstate', _handleBackdrop);
        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener('popstate', _handleBackdrop);
        };
        //eslint-disable-next-line
    }, [selectedTab]);

    function _handleBackdrop() {
        setProofModal(false);
        setAcceptModal(false);
        setRejectModal(false);
        if (document.querySelector(".modal-backdrop")) {
            document.querySelector(".modal-backdrop").remove();
            document.body.style.overflow = 'unset';
        };
    };

    // useEffect(() => {
    //     if (isClearRef.current === true) {
    //         _userList();

    //         isClearRef.current = false;
    //     }
    // }, [isClearRef.current]);


    useEffect(() => {
        if (acceptModal === true) {
            const accept_modal = new Modal(document.getElementById('accept-modal'));
            accept_modal.show();
        }
    }, [acceptModal]);

    useEffect(() => {
        if (rejectModal === true) {
            const reject_modal = new Modal(document.getElementById('reject-modal'));
            reject_modal.show();
        }
    }, [rejectModal]);

    useEffect(() => {
        if (proofModal === true) {
            const proof_modal = new Modal(document.getElementById('proof-modal'))
            proof_modal.show();
        }
    }, [proofModal]);

    useEffect(() => {
        if (state !== null) {
            if (state.tab_index) {
                setSelectedTab(state.tab_index);
            } else {
                setSelectedTab(1);
            }
            if (state.type) {
                setSelectedType(state.type)
            } else {
                setSelectedType("invested");
            }
            if (state.current_page) {
                setCurrentPage(state.current_page);
            } else {
                setCurrentPage(1);
            }
        } else {
            setSelectedTab(1);
            setCurrentPage(1);
            setSelectedType("invested");
        }
    }, [state]);


    // handle filter
    const _handleFilter = () => {
        setFilterBtn(!filterBtn);
    };

    // handle table page change 
    const _handleTablePageChange = (page_num) => {
        setLoader(true);
        setCurrentPage(page_num);
        _userList(page_num);

        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    };

    // handle navigate detail 
    const _handleDetail = (customer_id) => {
        navigate(`${customer_id}`, {
            state: {
                source: "customer_support",
                tab_index: selectedTab,
                type: selectedType,
                current_page: currentPage
            }
        });
    };

    // handle clear date
    const _handleClearDate = () => {
        setSelectedDate(null);
    };

    // handle clear filter
    const _handleClear = () => {
        setLoader(true);
        _handleClearDate();

        setCustomerCategory(filterData?.customer_category[0]);
        setCustomerType(filterData?.customer_type[0]);
        setSelectedKycStatus(filterData?.kyc_status[0]);


        isClearRef.current = true;
    };

    // handle selected filter
    const _handleFilterSelected = () => {
        setLoader(true);
        _userList(1);
        setCurrentPage(1);
    };

    // on search click
    const _onSearch = () => {
        setSearched(true);
        setLoader(true);
        _userList(1);
    };

    // handle search clear
    const _handleSearchClear = () => {
        setSearched(false);
        setSearchValue("");
        if (searched === true) {
            _userList(currentPage, "")
        }
    };

    // handle download list
    const _downloadListUser = () => {
        _downloadList();
    };


    // Pending request data table column 
    // const INVESTED = [
    //     {
    //         name: 'Name',
    //         selector: row => row.name,
    //         cell: (row) =>
    //             <span className='e-text-blue-violet text-truncate e-border-bottom-1px-blue-violet'
    //                 onClick={() => _handleDetail(row.customer_id)}>
    //                 {row.name}
    //             </span>,
    //         width: "250px",
    //         sortable: true,
    //     },
    //     {
    //         name: 'Customer ID',
    //         selector: row => row.rayi_customer_id,
    //         width: "150px",
    //     },
    //     {
    //         name: 'FP Customer ID',
    //         selector: row => "-",
    //         width: "150px",
    //     },
    //     {
    //         name: 'Email',
    //         selector: (row) => row.email,
    //         width: "320px"
    //     },
    //     {
    //         name: 'Phone number',
    //         selector: (row) => row.phone,
    //         width: "180px"
    //     },
    //     {
    //         name: 'Customer Type',
    //         selector: (row) => "-",
    //         width: "180px"
    //     },
    //     {
    //         name: 'Customer Category',
    //         selector: (row) => "-",
    //         width: "180px"
    //     },
    //     {
    //         name: 'Signed-Up Date',
    //         selector: row => new Date(row.joined_on),
    //         format: row => row.joined_on,
    //         width: "150px",
    //         sortable: true
    //     },
    //     {
    //         name: 'Signed-Up Status',
    //         selector: row => "-",
    //         width: "150px",
    //     },
    //     {
    //         name: 'KYC Submitted',
    //         selector: (row) => "-",
    //         width: "150px"
    //     },
    //     {
    //         name: 'KYC Status',
    //         selector: (row) => '-',
    //         width: "150px"
    //     },
    //     {
    //         name: 'AUM',
    //         selector: row => row.aum_text || '-',
    //         width: "170px",
    //         sortable: true,
    //         sortFunction: (a, b) => {
    //             if (a.aum < b.aum) return -1;
    //             if (a.aum > b.aum) return 1;
    //             return 0;
    //         }
    //     },
    //     {
    //         name: 'KYC Category',
    //         selector: (row) => '-',
    //         width: "150px"
    //     },
    // ];

    // const ALL_CUSTOMER = [

    //     {
    //         name: 'Name',
    //         selector: row => row.name,
    //         cell: (row) =>
    //             <span className='e-text-blue-violet text-truncate text-decoration-hover-underline cursor-pointer'
    //                 onClick={() => _handleDetail(row.customer_id)}>
    //                 {row.name || "-"}
    //             </span>,
    //         width: "280px",
    //         sortable: true,
    //     },
    //     {
    //         name: 'Customer ID',
    //         // selector: row => row.rayi_customer_id || "-",
    //         cell: (row) =>
    //             <span className='e-text-blue-violet text-truncate text-decoration-hover-underline cursor-pointer'
    //                 onClick={() => _handleDetail(row.customer_id)}>
    //                 {row.rayi_customer_id || "-"}
    //             </span>,
    //         width: "150px",
    //     },
    //     {
    //         name: 'FP Customer ID',
    //         selector: row => row.fp_customer_id || "-",
    //         width: "360px",
    //     },
    //     {
    //         name: 'Email',
    //         selector: (row) =>row.email,
    //         width: "320px"
    //     },
    //     {
    //         name: 'Phone number',
    //         selector: (row) => row.phone_number,
    //         width: "240px"
    //     },
    //     {
    //         name: 'Customer Type',
    //         selector: row => row.customer_type || "-",
    //         width: "180px",
    //     },
    //     {
    //         name: 'Customer Category',
    //         selector: row => row.customer_category || "-",
    //         width: "280px",
    //     },
    //     {
    //         name: 'Signed-Up Date',
    //         selector: row => new Date(row.signed_up_date),
    //         format: row => row.signed_up_date,
    //         width: "150px",
    //         sortable: true
    //     },
    //     {
    //         name: 'Signed-Up Status',
    //         selector: row => row.signed_up_status || "-",
    //         width: "280px",
    //     },

    //     {
    //         name: 'KYC Submitted Date',
    //         selector: row => row.kyc_submitted_date || "-",
    //         width: "280px",
    //     },
    //     {
    //         name: 'KYC Status',
    //         selector: row => row.kyc_status || "-",
    //         width: "150px",
    //     },
    //     {
    //         name: 'AUM',
    //         selector: row => row.aum,
    //         width: "170px",
    //         sortable: true,
    //         sortFunction: (a, b) => {
    //             const numA = Number(a.aum.replace(/[^0-9.-]+/g, "")) || 0;
    //             const numB = Number(b.aum.replace(/[^0-9.-]+/g, "")) || 0;
    //             return numA - numB;
    //         },
    //         cell: row => (
    //             row.aum ? row.aum : '-'
    //         )
    //     },
    //     // {
    //     //     name: 'KYC Category',
    //     //     selector: row => row.kyc_category || "-",
    //     //     width: "150px",
    //     // },
    // ];

    const th = (label) => <span className="e-table-th-wrap">{label}</span>;
    const thLines = (...lines) => (
        <span className="e-table-th-wrap">
            {lines.map((line, i) => (
                <React.Fragment key={i}>
                    {i > 0 ? <br /> : null}
                    {line}
                </React.Fragment>
            ))}
        </span>
    );

    const ALL_CUSTOMER = [
        {
            name: thLines('Signed-Up', 'Date'),
            selector: row => row.signed_up_date || "-",
            cell: (row) => <span className='text-truncate d-block' title={row.signed_up_date || undefined}>{row.signed_up_date || "-"}</span>,
            format: row => row.signed_up_date,
            sortable: true,
            width: "180px",
            id: "signed_up_date",
        },
        {
            name: th('Customer ID'),
            selector: row => row.customer_id || "-",
            cell: (row) => <span className='e-text-blue-violet text-truncate text-decoration-hover-underline cursor-pointer'
                title={row.rayi_customer_id || undefined}
                onClick={() => _handleDetail(row.customer_id)}>
                {row.rayi_customer_id || "-"}
            </span>,
            width: "130px",
            sortable: true,
            id: "customer_id",
        },
        {
            name: th('Name'),
            selector: row => row.name,
            cell: (row) =>
                <span title={row.name} className='e-text-blue-violet text-truncate text-decoration-hover-underline cursor-pointer '
                    onClick={() => _handleDetail(row.customer_id)}>
                    {row.name || "-"}
                </span>,
            width: "260px",
            sortable: true,
            id: "name",
        },
        {
            name: thLines('Customer', 'Type'),
            selector: row => row.customer_type || "-",
            cell: row => <span className='text-truncate d-block' title={row.customer_type || undefined}>{row.customer_type || "-"}</span>,
            width: "120px",
            id: "customer_type",
        },
        {
            name: thLines('Customer', 'Category'),
            selector: row => row.customer_category || "-",
            cell: row => <span className='text-truncate' title={row.customer_category}>{row.customer_category || "-"}</span>,
            width: "140px",
            id: "customer_category",
        },
        {
            name: thLines('Signed-Up', 'Status'),
            selector: row => row.signed_up_status || "-",
            cell: row => <span className='text-truncate d-block' title={row.signed_up_status || undefined}>{row.signed_up_status || "-"}</span>,
            width: "240px",
            id: "signed_up_status",
        },

        {
            name: thLines('KYC Submitted', 'Date'),
            selector: row => row.kyc_submitted_date || "-",
            cell: (row) => <span className='text-truncate d-block' title={row.kyc_submitted_date || undefined}>{row.kyc_submitted_date || "-"}</span>,
            width: "170px",
            format: row => row.kyc_submitted_date,
            sortable: true,
            id: "kyc_submitted_date",
        },
        {
            name: th('KYC Status'),
            selector: row => row.kyc_status || "-",
            cell: row => <span className='text-truncate d-block' title={row.kyc_status || undefined}>{row.kyc_status || "-"}</span>,
            width: "120px",
            id: "kyc_status",
        },
        {
            name: th('KRA Status'),
            cell: row => <span className='text-truncate d-block' title={row.kra_status || undefined}>{row.kra_status || "-"}</span>,
            width: "130px",
            id: "kra_status",
        },


        {
            name: th('Email'),
            cell: (row) =>
                <a title={row.email} href={`mailto:${row.email}`} target='_blank' className='e-text-smoky-black text-decoration-none text-truncate text-decoration-hover-underline'>
                    {row.email}
                </a>,
            width: "240px",
            id: "email",
        },
        {
            name: thLines('Phone', 'number'),
            cell: (row) => <>
                {
                    row.phone_number ?
                        <a href={`tel:${row.phone_number}`} title={row.phone_number} className='text-truncate e-text-smoky-black text-decoration-none text-decoration-hover-underline'>
                            {row.phone_number}
                        </a>
                        :
                        <>
                            -
                        </>
                }
            </>,
            width: "130px",
            id: "phone_number",
        },
        {
            name: th('AUM'),
            selector: row => row.aum,
            width: "130px",
            sortable: true,
            sortFunction: (a, b) => {
                const numA = Number(a.aum.replace(/[^0-9.-]+/g, "")) || 0;
                const numB = Number(b.aum.replace(/[^0-9.-]+/g, "")) || 0;
                return numA - numB;
            },
            cell: row => (
                <span className='text-truncate' title={row.aum}>{row.aum || "-"}</span>
            ),
            id: "aum",
        },
        {
            name: thLines('FP Customer', 'ID'),
            selector: row => row.fp_customer_id || "-",
            cell: row => <span className='text-truncate' title={row.fp_customer_id}>{row.fp_customer_id || "-"}</span>,
            width: "340px",
            id: "fp_customer_id",
        },
        // {
        //     name: 'KYC Category',
        //     selector: row => row.kyc_category || "-",
        //     width: "150px",
        // },
        {
            name: th('User status'),
            cell: (row) =>
                <>
                    {row.is_deleted === true ?
                        <Chip label={"Deleted"}
                            className={"e-text-dark-gray e-bg-pale-pink"} />
                        :
                        "-"
                    }
                </>,
            width: "130px",
            id: "user_status",
        },
        {
            name: thLines('Last Status', 'Updated'),
            selector: row => row.kyc_succeeded_at || "-",
            cell: row => <span className='text-truncate' title={row.kyc_succeeded_at}>{row.kyc_succeeded_at || "-"}</span>,
            width: "180px",
            id: "last_status_updated",
        },
    ];

    // API - Get login otp
    const _filterData = () => {
        let is_post = false;
        let url = 'user/filter-data';
        APIService(is_post, url).then((response) => {
            if (response.status_code === 200) {
                setFilterData(response.data);

                setCustomerCategory(response.data?.customer_category[0]);
                setCustomerType(response.data?.customer_type[0]);
                setSelectedKycStatus(response.data?.kyc_status[0]);
            } else {
                setFilterData([]);
            }
            setFilterLoader(false);
        });
    };

    // API -  user listing
    const _userList = (page_num = currentPage, search = searchValue) => {

        const formatted_start_date = selectedDate === null ? null : format(new Date(selectedDate[0]), 'dd-MM-yyyy');
        const formatted_end_date = selectedDate === null ? null : format(new Date(selectedDate[1]), 'dd-MM-yyyy');

        let is_post = true;
        let url = 'support-customers/list';
        let request = {
            "query": search,
            "page_num": page_num,
            "page_size": 10,
            "customer_category": customerCategory !== null ? customerCategory?.value : null,
            "customer_type": customerType !== null ? customerType?.value : null,
            "kyc_status": selectedKycStatus !== null ? selectedKycStatus?.value : null,
            "kyc_submitted_start_date": formatted_start_date,
            "kyc_submitted_end_date": formatted_end_date
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setListData(response.data);
            } else {
                setListData({ data: [] });
            }
            setLoader(false);
        });
    };

    // API - Bank verification approve
    const _bankVerificationApprove = () => {
        let is_post = true;
        let url = 'user/approve-bank-verification';
        let request = {
            "customer_id": selectedRow.customer_id,
            "bank_id": selectedRow.bank_id
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setAcceptModal(false);
                _userList();
            } else {
                toast.dismiss();
                toast.success(response.message, { type: "error" });
            }
        })
    };

    // API - Bank verification reject
    const _bankVerificationReject = (note) => {
        let is_post = true;
        let url = 'user/reject-bank-verification';
        let request = {
            "customer_id": selectedRow.customer_id,
            "bank_id": selectedRow.bank_id,
            "rejection_reason": note
        }
        setRejectLoader(true);
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setRejectSuccess(true);
                toast.dismiss();
                toast.success("Successfully rejected", { type: "success" });
            } else {
                toast.dismiss();
                toast.error(response.message, { type: "error" });
            }
            setRejectLoader(false);
        })
    };


    // API - Download list customers
    const _downloadList = () => {
        let is_post = true;
        let url = 'user/list-download';
        let request = {
            "user_ids": checked,
            "query": searchValue,
            "investment_status": selectedTab,// 1 - invested , 2- non-invested, 3-signed-up, 4 - fresh kyc , 5- bank verfication
            "kyc_status": selectedKycStatus.value,
            "risk_profile": selectedRiskAppetite.value,
            "joined_date": selectedDate,
            "fp_status": selectedFpStatus.value,
            "status": selectedStatus.value
        }
        setDownloadLoader(true);
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                // window.open(response.data.url, '_blank');

                const url = response.data.url;
                const link = document.createElement('a');
                link.href = url;
                link.download = ''; // Optional: specify a file name
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                setChecked([]);
            } else {
                toast.dismiss();
                toast.success(response.message, { type: "error" })
            }
            setDownloadLoader(false);
        })
    };

    return (
        <>
            <section className='e-section-height pb-5 e-bg-magnolia' >
                <div className='e-bg-lavender padding-52px-lr pt-2 padding-80px-bottom'>
                    <h3 className='margin-32px-bottom e-text-chinese-black e-primary-font e-font-24 e-font-weight-600 line-height-24px'>
                        Support Customers
                    </h3>
                </div>

                <div className='padding-52px-lr margin--80px-top'>
                    <div className='e-bg-ghost-white border-radius-16  padding-32px-bottom'>
                        <div ref={scrollRef} className='padding-32px-lr padding-32px-top'>

                            <div className='d-flex pb-2 align-items-center justify-content-end gap-32px'>

                                {/* <SecondaryButton name="Download List"
                                    disabled={loader === false && listData.data.length === 0 ? true : false}
                                    loader={downloadLoader}
                                    className={downloadLoader ? "w-129px h-42px" : ""}
                                    onClick={_downloadListUser} /> */}

                                <CustomSearchInput placeholder="Search with user’s name, email, mobile number or customer ID"
                                    type="text"
                                    value={searchValue}
                                    onSearch={() => _onSearch()}
                                    onClear={_handleSearchClear}
                                    onSearchClear={_handleSearchClear}
                                    className="w-380px"
                                    onKeyPress={() => _onSearch()}
                                    handleChange={(e) => {
                                        setSearchValue(e);
                                        if (e.length === 0) {
                                            _handleSearchClear()
                                        }
                                    }} />

                                <div className={` padding-10px-all border-radius-8 e-cursor-pointer e-border-1px-light-purple cursor-pointer   ${filterBtn ? ' e-bg-blue-violet ' : ''}`}
                                    onClick={filterLoader === false && _handleFilter}>
                                    <Icon icon='filter'
                                        size='1.25rem'
                                        className='d-flex'
                                        color={filterBtn && color.white} />
                                </div>
                            </div>
                        </div>

                        <div className={`e-filter-content  ${filterBtn ? 'expanded ' : 'e-overflow collapsed'}`}>
                            <div className='e-bg-main-white pt-3 pb-3 padding-32px-lr'>
                                <div className='d-flex justify-content-end'>
                                    <TextButton name="Clear all"
                                        onClick={_handleClear}
                                    />
                                </div>
                                <div className='d-flex gap-4 padding-32px-bottom'>
                                    <CustomSelectBox
                                        title={"Customer category"}
                                        placeholder='Select'
                                        controlWidth='11rem'
                                        options={filterData?.customer_category}
                                        isSearchable={false}
                                        loader={filterLoader}
                                        value={customerCategory}
                                        onChange={(value) => setCustomerCategory(value)} />

                                    <CustomSelectBox
                                        title={"Customer type"}
                                        placeholder='Select'
                                        controlWidth='11rem'
                                        options={filterData?.customer_type}
                                        isSearchable={false}
                                        loader={filterLoader}
                                        value={customerType}
                                        onChange={(value) => setCustomerType(value)} />

                                    <CustomSelectBox
                                        title={"KYC status"}
                                        placeholder='Select'
                                        controlWidth='11rem'
                                        options={filterData?.kyc_status}
                                        isSearchable={false}
                                        loader={filterLoader}
                                        value={selectedKycStatus}
                                        onChange={(value) => setSelectedKycStatus(value)} />

                                    <DateRangePicker label="KYC submitted Date"
                                        placeholder="Select"
                                        calendarWrapperClass="end-0"
                                        value={selectedDate}
                                        maxDate={new Date}
                                        handleClear={() => setSelectedDate(null)}
                                        handleChange={(date) => setSelectedDate(date)} />
                                </div>

                                <PrimaryButton name='Filter now'
                                    paddingBlock='padding-12px-tb padding-40px-lr'
                                    onClick={_handleFilterSelected} />
                            </div>
                        </div>


                        {(searched || filterBtn === true) &&
                            <>
                                <div className='pt-2 padding-32px-lr d-flex gap-1 align-items-center justify-content-end'>
                                    <div>
                                        <img src={require("../../Assets/Image/scroll-animation.gif")}
                                            draggable={false}
                                            className='e-rotate-270'
                                            width={24}
                                            height={24}
                                            alt='scroll' />
                                    </div>
                                    <p className='mb-0 e-text-bleu-de-france e-alt-font e-font-12 e-font-weight-500 line-height-14px'>
                                        Scroll horizontally for view more  information
                                    </p>
                                </div>

                                {loader === true ?
                                    <div className='d-flex align-items-center min-vh-50 justify-content-center'>
                                        <img src={require("../../Assets/Image/loader.gif")}
                                            draggable={false}
                                            width={80}
                                            height={80}
                                            alt='loader'
                                            className='text-center' />
                                    </div>
                                    :
                                    listData?.data?.length !== 0 ?
                                        <div className='e-data-table-wrapper-default-updated pt-3 '>
                                            <CustomDataTable columns={ALL_CUSTOMER}
                                                data={listData.data}
                                                pagination={true}
                                                paginationPerPage={10}
                                                paginationDefaultPage={listData.current_page}
                                                paginationTotalRows={listData.total_count}
                                                lastPage={listData.total_page}
                                                onChangePage={_handleTablePageChange}
                                            // rowClick={(row) => _handleDetail(row.customer_id, row.name)}
                                            />
                                        </div>
                                        :
                                        <EmptyScreen
                                            image={require('../../Assets/Image/NotFound/no-data.png')}
                                            title='No results found'
                                            description={`We couldn' t find anything that matches your search. Try checking your spelling, using different keywords, or exploring popular funds.`}
                                        />
                                }
                            </>
                        }

                    </div>
                </div >
            </section >

            {proofModal === true &&
                <ViewDetailModal
                    data={selectedRow}
                    handleClose={() => {
                        setProofModal(false);
                    }}
                />
            }

            {/*  accept modal */}
            {
                acceptModal === true &&
                <AcceptModal
                    data={selectedRow}
                    handleConfirm={() => _bankVerificationApprove()}
                    handleClose={() => {
                        setAcceptModal(false);
                    }}

                />
            }

            {/* reject modal */}
            {rejectModal === true &&
                <RejectModal
                    data={selectedRow}
                    status={rejectSuccess}
                    loader={rejectLoader}
                    handleConfirm={(note) => _bankVerificationReject(note)}
                    handleClose={() => {
                        setRejectModal(false);
                        setRejectSuccess(false);
                    }}
                />
            }
        </>
    )
}

export default CustomerSupport;